import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../db/database'
import {
  daysSinceLastSync, getSyncMode, getToken, setSyncMode, syncNow, type SyncMode,
} from '../db/sync'
import { restartOnboarding } from '../db/onboarding'
import type { RedoMode } from '../db/marks'
import { useCycleLocked } from '../lib/lock'
import { AccountCard } from '../components/AccountCard'
import { ConflictsSheet } from '../components/ConflictsSheet'
import { storageState, type StorageState } from '../lib/storage'
import { Icon } from '../components/Icon'
import { formatFullDate, getDayShift, setDayShift, today } from '../lib/dates'
import { getThemeMode, setThemeMode, type ThemeMode } from '../lib/theme'
import {
  installMode, isInstalledElsewhere, manualHint, onInstallChange, promptInstall,
  type InstallMode,
} from '../lib/install'
import { useAppUpdate } from '../lib/pwa'
import './ProfileScreen.css'

/**
 * Le seul endroit où le réseau est un sujet visible.
 *
 * Le mode est PAR APPAREIL (décision vision.md) : c'est un choix de coût de
 * données, pas un réglage de compte. En mode Offline, aucune synchronisation
 * n'est tentée même avec du réseau — ce qui est un vrai enjeu de facture à
 * Madagascar.
 */
export function ProfileScreen(
  { walletId, onSignedOut }: {
    walletId: string
    /** Quitter le compte, ou le supprimer : l'app doit revenir au portail. */
    onSignedOut: () => void
  },
) {
  const [mode, setMode] = useState<SyncMode>(getSyncMode)
  const [theme, setTheme] = useState<ThemeMode>(getThemeMode)
  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [days, setDays] = useState<number | null>(null)
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [tranchant, setTranchant] = useState(false)
  // La portée par défaut est la moins destructrice : un choix par défaut se
  // valide sans être lu, celui-là ne doit rien effacer.
  const [redo, setRedo] = useState<RedoMode>('settings')
  const locked = useCycleLocked()

  // Le jeton d'appareil, obtenu au portail d'entrée (décision 2026-08-24). Il
  // reste possible de ne pas en avoir — une base d'avant l'authentification,
  // par exemple — et l'écran doit alors le dire plutôt que d'offrir un bouton
  // qui échoue.
  const connected = getToken() !== null

  // L'état du stockage se demande au navigateur, il ne se déduit de rien.
  const [stockage, setStockage] = useState<StorageState>('best-effort')
  useEffect(() => { void storageState().then(setStockage) }, [])

  const pending = useLiveQuery(() => db.outbox.count(), [], 0)
  const conflicts = useLiveQuery(() => db.conflicts.count(), [], 0)
  const lastSync = useLiveQuery(
    async () => (await db.meta.get('last_sync'))?.value as string | undefined, [],
  )

  useEffect(() => { void daysSinceLastSync().then(setDays) }, [lastSync])

  function choose(next: SyncMode) {
    setSyncMode(next)
    setMode(next)
  }

  function chooseTheme(next: ThemeMode) {
    setThemeMode(next)
    setTheme(next)
  }

  // L'invite d'installation n'arrive pas forcément avant cet écran : le
  // navigateur la tire quand il l'a décidé, parfois plusieurs secondes après
  // le chargement. On s'abonne plutôt que de lire une seule fois.
  const [install, setInstall] = useState<InstallMode>(installMode)
  const [justInstalled, setJustInstalled] = useState(false)
  useEffect(() => onInstallChange(() => setInstall(installMode())), [])

  // Posée ailleurs, alors qu'on la regarde ici dans un onglet : Chrome cesse
  // d'émettre l'invite dans ce cas, et l'écran conseillerait sinon d'installer
  // ce qui l'est déjà. La question se pose au navigateur, elle n'est pas
  // déductible.
  const [alreadyInstalled, setAlreadyInstalled] = useState(false)
  useEffect(() => { void isInstalledElsewhere().then(setAlreadyInstalled) }, [])

  const installed = justInstalled || alreadyInstalled || install === 'installed'

  const { status, checkForUpdate, applyUpdate } = useAppUpdate()
  const looking = status === 'checking' || status === 'downloading'

  async function startInstall() {
    // ⚠️ Juste après l'acceptation, la page tourne TOUJOURS dans l'onglet :
    // `isStandalone()` reste faux et l'invite est consommée. Sans cet état,
    // l'écran retomberait sur « installe-la depuis le menu » — le contraire
    // de ce qui vient de se passer.
    if (await promptInstall()) setJustInstalled(true)
  }

  async function sync() {
    setSyncing(true)
    setSyncError(null)
    try {
      await syncNow()
    } catch {
      /*
       * ⚠️ Un bouton qui ne dit rien quand il échoue se lit comme une app
       * cassée — et c'est exactement ce qui s'est produit en production le
       * 2026-09-06 : le pull sortait en silence sur un 304, rien ne bougeait
       * à l'écran, pas même la date. Mieux vaut une phrase que le doute.
       */
      setSyncError("La synchronisation n'a pas abouti. Réessaie dans un moment.")
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div className="screen">
      <header className="profile-head">
        <p>Gère tes informations personnelles et tes préférences.</p>
      </header>

      {/* Le compte en PREMIER : c'est la seule chose de cet onglet qui
          identifie l'utilisateur, et « c'est bien mon compte ? » est la
          première question qu'on se pose en l'ouvrant. */}
      <AccountCard onSignedOut={onSignedOut} />

      {/* Avertissement à 3 jours sans synchronisation (décision vision.md) :
          l'enjeu est de ne pas découvrir trop tard un gros décalage. */}
      {days !== null && days >= 3 && (
        <p className="warning">
          Aucune synchronisation depuis {days} jours. Vos données ne sont
          enregistrées que sur cet appareil.
        </p>
      )}

      <section className="card">
        <h2>Synchronisation</h2>
        <p className="card-help">
          Ce réglage ne vaut que pour cet appareil.
        </p>

        <div className="modes" role="group" aria-label="Mode de synchronisation">
          <button
            type="button"
            className={mode === 'online' ? 'is-selected' : ''}
            aria-pressed={mode === 'online'}
            onClick={() => choose('online')}
          >
            <Icon name="cloud" size={18} />
            <span>
              <strong>En ligne</strong>
              <small>Envoi automatique à chaque saisie</small>
            </span>
          </button>
          <button
            type="button"
            className={mode === 'offline' ? 'is-selected' : ''}
            aria-pressed={mode === 'offline'}
            onClick={() => choose('offline')}
          >
            <Icon name="cloudOff" size={18} />
            <span>
              <strong>Hors ligne</strong>
              <small>Rien n'est envoyé sans votre accord</small>
            </span>
          </button>
        </div>

        {connected ? (
          <>
            <button type="button" className="sync-now" onClick={sync} disabled={syncing}>
              {syncing ? 'Synchronisation…' : 'Synchroniser maintenant'}
            </button>
            {syncError && <p className="warning" role="alert">{syncError}</p>}
          </>
        ) : (
          /* Un bouton qui ne peut pas aboutir est pire que pas de bouton : on
             clique, rien ne se passe, et on conclut que l'app est cassée. */
          <p className="sync-unavailable">
            <strong>Cet appareil n'est rattaché à aucun compte.</strong>
            Vos saisies sont conservées ici et partiront intactes dès que vous
            vous connecterez — rien ne se perd.
          </p>
        )}

        <dl className="facts">
          <div><dt>En attente d'envoi</dt><dd className="tabular">{pending}</dd></div>
          <div>
            <dt>Dernière synchronisation</dt>
            <dd>{lastSync ? new Date(lastSync).toLocaleString('fr-MG') : 'jamais'}</dd>
          </div>
        </dl>

        {/* Le moteur détectait et stockait les conflits depuis le début, mais
            rien ne les présentait : ils s'accumulaient dans une table que
            personne ne lisait. Un désaccord qu'on ne montre pas est un
            désaccord tranché au hasard. */}
        {conflicts > 0 && (
          <button type="button" className="conflicts-open"
                  onClick={() => setTranchant(true)}>
            <Icon name="cloudOff" size={16} />
            {conflicts === 1
              ? 'Un désaccord à trancher'
              : `${conflicts} désaccords à trancher`}
          </button>
        )}
      </section>

      <section className="card">
        <h2>Apparence</h2>
        <p className="card-help">
          Ce réglage ne vaut que pour cet appareil.
        </p>

        <div className="modes" role="group" aria-label="Thème de l'application">
          <button
            type="button"
            className={theme === 'system' ? 'is-selected' : ''}
            aria-pressed={theme === 'system'}
            onClick={() => chooseTheme('system')}
          >
            <Icon name="monitor" size={18} />
            <span>
              <strong>Système</strong>
              <small>Suit le réglage de l'appareil</small>
            </span>
          </button>
          <button
            type="button"
            className={theme === 'light' ? 'is-selected' : ''}
            aria-pressed={theme === 'light'}
            onClick={() => chooseTheme('light')}
          >
            <Icon name="sun" size={18} />
            <span>
              <strong>Clair</strong>
            </span>
          </button>
          <button
            type="button"
            className={theme === 'dark' ? 'is-selected' : ''}
            aria-pressed={theme === 'dark'}
            onClick={() => chooseTheme('dark')}
          >
            <Icon name="moon" size={18} />
            <span>
              <strong>Sombre</strong>
            </span>
          </button>
        </div>
      </section>

      <section className="card">
        <h2>L'application</h2>

        {/* L'installation. Un seul de ces cas s'affiche à la fois — voir
            `lib/install.ts` pour pourquoi certains navigateurs n'ont pas de
            bouton : l'invite n'existe que chez Chrome et Edge. */}
        {installed ? (
          <p className="card-help">
            <Icon name="check" size={14} /> L'app est installée sur cet appareil.
          </p>
        ) : install === 'prompt' ? (
          <>
            <p className="card-help">
              Installée, elle s'ouvre depuis ton écran d'accueil et démarre sans réseau.
            </p>
            <button type="button" className="card-action" onClick={() => void startInstall()}>
              Installer l'app sur cet appareil
            </button>
          </>
        ) : (
          <p className="card-help"><InstallSteps /></p>
        )}

        {/* La mise à jour. Aucun pourcentage : le service worker ne remonte
            pas l'avancement de son téléchargement (voir `lib/pwa.ts`). */}
        <div className="update-check">
          <button type="button" className="card-action is-quiet"
                  onClick={() => void checkForUpdate()} disabled={looking}>
            {looking ? 'Vérification…' : 'Vérifier les mises à jour'}
          </button>

          {looking && (
            <div className="progress" role="progressbar" aria-label="Mise à jour en cours">
              <span />
            </div>
          )}

          {status !== 'idle' && (
            <p className={`update-state is-${status}`} role="status">
              {status === 'checking' && 'Recherche d’une nouvelle version…'}
              {status === 'downloading' && 'Téléchargement de la nouvelle version…'}
              {status === 'ready' && 'Une nouvelle version est prête.'}
              {status === 'current' && 'Tu utilises déjà la dernière version.'}
              {status === 'offline' && 'Pas de réseau : impossible de vérifier maintenant.'}
            </p>
          )}

          {status === 'ready' && (
            <button type="button" className="card-action" onClick={applyUpdate}>
              Mettre à jour maintenant
            </button>
          )}
        </div>
      </section>

      <section className="card">
        <h2>Sauvegarde locale</h2>
        {/* Sans persistance accordée, le navigateur s'autorise à effacer la base
            quand l'appareil manque de place. Le taire laisserait l'utilisateur
            croire ses données à l'abri alors qu'elles ne le sont pas. */}
        <p className="card-help">
          {stockage === 'persisted'
            ? 'Le navigateur a accepté de conserver tes données sur cet appareil.'
            : stockage === 'unsupported'
              ? "Ce navigateur ne sait pas garantir la conservation des données. Synchronise régulièrement."
              : "Le navigateur ne garantit pas encore de conserver tes données si l'appareil manque de place. Installe l'app sur ton écran d'accueil, et synchronise régulièrement."}
        </p>
      </section>

      {tranchant && <ConflictsSheet onClose={() => setTranchant(false)} />}

      {/* Voyage dans le temps, développement uniquement.

          Une app dont tout dépend du cycle ne se teste pas sans avancer la
          date : la clôture, le revenu reproposé, les journées en retard ne se
          déclenchent qu'au passage d'une frontière de cycle. */}
      {import.meta.env.DEV && (
        <section className="card">
          <h2>Voyage dans le temps</h2>
          <p className="card-help">
            L'app se croit le <strong>{formatFullDate(today())}</strong>.
            {getDayShift() !== 0 && ` (${getDayShift() > 0 ? '+' : ''}${getDayShift()} jours)`}
          </p>
          <div className="time-travel">
            {[
              { label: '+1 jour', days: 1 },
              { label: '+1 semaine', days: 7 },
              { label: '+1 mois', days: 31 },
            ].map((saut) => (
              <button key={saut.days} type="button"
                      onClick={() => { setDayShift(getDayShift() + saut.days); location.reload() }}>
                {saut.label}
              </button>
            ))}
            <button type="button" onClick={() => { setDayShift(getDayShift() - 1); location.reload() }}>
              −1 jour
            </button>
            <button type="button" className="time-reset"
                    onClick={() => { setDayShift(0); location.reload() }}>
              Aujourd'hui
            </button>
          </div>
        </section>
      )}

      {/*
        Une seule carte, deux portées.

        « Je me suis trompé » recouvre deux situations très différentes :
        corriger un rythme mal choisi alors que le mois se passe bien, et vouloir
        recommencer une période partie de travers. Deux cartes séparées les
        présentaient comme deux fonctions distinctes, alors que c'est UNE
        décision — jusqu'où revenir en arrière — et qu'on la prend en comparant
        les deux réponses côte à côte.

        Rien n'est effacé ici : le choix ouvre les deux questions du premier
        lancement, et c'est en y répondant que la reprise s'applique. Refermer la
        feuille laisse les données intactes.
      */}
      <section className="card">
        <h2>Recommencer le réglage</h2>
        <p className="card-help">
          Le rythme et le montant de départ se choisissent au tout premier écran,
          avant d'avoir rien vu de l'app. Voici comment y revenir.
        </p>

        {/* `aria-label` et non un `<legend>` masqué : dans un fieldset en
            grille, la position statique d'un legend absolu tombe hors du
            conteneur qui défile, et la PAGE gagnait une barre de défilement de
            23 px. Constaté le 2026-08-23. */}
        <fieldset className="redo-choices" disabled={locked}
                  aria-label="Jusqu'où revenir en arrière">
          <label>
            <input type="radio" name="redo" checked={redo === 'settings'}
                   onChange={() => setRedo('settings')} />
            <span>
              <strong>Garder ce que j'ai saisi</strong>
              Tes opérations, tes catégories et tes enveloppes restent en place.
              Seul l'argent d'ouverture est remplacé par le nouveau montant.
            </span>
          </label>
          <label>
            <input type="radio" name="redo" checked={redo === 'cycle'}
                   onChange={() => setRedo('cycle')} />
            <span>
              <strong>Effacer la période en cours</strong>
              Les opérations, les enveloppes et les journées prévues du cycle en
              cours s'en vont. Les cycles déjà terminés ne bougent pas.
            </span>
          </label>
        </fieldset>

        <button type="button" className={`redo ${redo === 'cycle' ? 'is-heavy' : ''}`}
                disabled={locked}
                onClick={() => void restartOnboarding(walletId, redo)}>
          Reposer les questions
        </button>
      </section>

      {/* Uniquement en développement : `import.meta.env.DEV` est remplacé par
          `false` à la compilation, et le bloc disparaît du paquet livré. Un
          bouton qui efface tout n'a rien à faire dans les mains d'un
          utilisateur qui y range son argent. */}
      {import.meta.env.DEV && (
        <section className="card">
          <h2>Développement</h2>
          <p className="card-help">
            Efface la base locale et recharge l'application. Ce bloc n'existe
            pas dans l'application livrée.
          </p>
          {confirmingReset ? (
            <div className="reset-confirm">
              <button type="button" className="reset-go"
                      onClick={() => void resetEverything()}>
                Effacer pour de bon
              </button>
              <button type="button" className="reset-cancel"
                      onClick={() => setConfirmingReset(false)}>
                Annuler
              </button>
            </div>
          ) : (
            <button type="button" className="reset" onClick={() => setConfirmingReset(true)}>
              Repartir de zéro
            </button>
          )}
        </section>
      )}
    </div>
  )
}

/**
 * Les gestes d'installation, propres à CE navigateur.
 *
 * Un bouton est impossible ici : l'invite programmatique n'existe que chez
 * Chrome et Edge (`beforeinstallprompt`), et ce composant n'est rendu que
 * lorsqu'elle n'est pas venue. Reste à nommer le chemin exact — ou à dire
 * franchement que ce navigateur-ci ne sait pas installer, plutôt que
 * d'envoyer chercher une entrée de menu qui n'existe pas.
 */
function InstallSteps() {
  switch (manualHint()) {
    case 'ios':
      return (
        <>
          Pour l'installer : touche <strong>Partager</strong> en bas de Safari, puis
          {' '}<strong>Sur l'écran d'accueil</strong>. Apple ne permet pas de le faire
          depuis un bouton.
        </>
      )
    case 'firefox-android':
      return (
        <>
          Pour l'installer : ouvre le menu <strong>⋮</strong> de Firefox, puis
          {' '}<strong>Ajouter à l'écran d'accueil</strong>.
        </>
      )
    case 'firefox-desktop':
      return (
        <>
          Firefox sur ordinateur ne sait pas installer d'application web. Ouvre
          {' '}<strong>app.ariariko.mg</strong> dans Chrome ou Edge pour l'installer.
        </>
      )
    case 'safari-desktop':
      return (
        <>
          Pour l'installer : dans la barre de menus, <strong>Fichier</strong> puis
          {' '}<strong>Ajouter au Dock</strong>.
        </>
      )
    case 'in-app':
      return (
        <>
          Tu es dans le navigateur intégré d'une autre application, qui ne permet pas
          d'installer. Ouvre <strong>app.ariariko.mg</strong> dans Chrome ou Safari.
        </>
      )
    case 'chromium':
      return (
        <>
          Pour l'installer : clique l'icône d'installation dans la barre d'adresse, ou
          le menu <strong>⋮</strong> puis <strong>Installer l'application</strong>.
        </>
      )
    default:
      return (
        <>
          Pour l'installer : ouvre <strong>app.ariariko.mg</strong> dans Chrome, Edge ou
          Safari, puis choisis d'ajouter la page à ton écran d'accueil.
        </>
      )
  }
}

/**
 * Remet l'appareil dans l'état d'une première ouverture.
 *
 * `db.delete()` d'abord, `localStorage` ensuite, rechargement en dernier :
 * l'identifiant du portefeuille vit dans Dexie et le jeton dans localStorage.
 * Vider l'un sans l'autre laisserait l'app pointer vers un portefeuille
 * disparu.
 */
async function resetEverything(): Promise<void> {
  await db.delete()
  try { localStorage.clear() } catch { /* navigation privée */ }
  location.reload()
}
